import React, { useState, useEffect } from 'react';

export const colorPalette = {
  "Neutrals": [
    // Whites & Creams
    { name: "White", hex: "#FFFFFF" },
    { name: "Cream", hex: "#FFFDD0" },
    { name: "Ivory", hex: "#FFFFF0" },
    // Beiges & Tans
    { name: "Oatmeal", hex: "#E9DCC9" },
    { name: "Beige", hex: "#F5F5DC" },
    { name: "Camel", hex: "#C19A6B" },
    // Grays
    { name: "Silver", hex: "#C0C0C0" },
    { name: "Pewter", hex: "#96A8A1" },
    { name: "Charcoal", hex: "#36454F" },
    // Black
    { name: "Black", hex: "#000000" },
  ],

  "Classic Brights": [
    { name: "Cherry Red", hex: "#DE3163" },
    { name: "Royal Blue", hex: "#0047AB" },
    { name: "Kelly Green", hex: "#4CBB17" },
    { name: "Sunshine Yellow", hex: "#FFD700" },
    { name: "Bright Orange", hex: "#FF6600" },
    { name: "Hot Pink", hex: "#FF69B4" },
  ],

  "Earth Tones": [
    // Greens
    { name: "Olive", hex: "#808000" },
    { name: "Sage", hex: "#9CAF88" },
    { name: "Moss", hex: "#8A9A5B" },
    // Oranges/Reds
    { name: "Terracotta", hex: "#E2725B" },
    { name: "Rust", hex: "#B7410E" },
    { name: "Clay", hex: "#C96A4E" },
    // Yellows
    { name: "Mustard", hex: "#FFDB58" },
    { name: "Ochre", hex: "#CC7722" },
    // Browns
    { name: "Walnut", hex: "#5D3A1A" },
    { name: "Chestnut", hex: "#954535" },
    { name: "Espresso", hex: "#4A2C2A" },
  ],

  "Pastels": [
    { name: "Baby Pink", hex: "#F4C2C2" },
    { name: "Powder Blue", hex: "#B0E0E6" },
    { name: "Mint", hex: "#98FF98" },
    { name: "Lavender", hex: "#E6E6FA" },
    { name: "Buttercream", hex: "#FFFACD" },
  ],

  "Jewel Tones": [
    { name: "Deep Teal", hex: "#008080" },
    { name: "Sapphire", hex: "#0F52BA" },
    { name: "Amethyst", hex: "#9966CC" },
    { name: "Ruby", hex: "#E0115F" },
    { name: "Emerald", hex: "#50C878" },
  ],

  "Dusty Modern Tones": [
    { name: "Dusty Rose", hex: "#DCAE96" },
    { name: "Mauve", hex: "#E0B0FF" },
    { name: "Slate Blue", hex: "#6A7B8C" },
    { name: "Dusty Teal", hex: "#5F9EA0" },
    { name: "Muted Plum", hex: "#8E4585" },
  ],

  "Gradients & Ombres": [
    { name: "Grey to Black", hex: "linear-gradient(90deg, #808080, #000000)" },
    { name: "Cream to Brown", hex: "linear-gradient(90deg, #FFFDD0, #D2B48C, #8B4513)" },
    { name: "Pink to Purple", hex: "linear-gradient(90deg, #FFC0CB, #E0B0FF, #800080)" },
    { name: "Blue to Teal", hex: "linear-gradient(90deg, #0000FF, #4682B4, #008080)" },
  ],

  "Novelty & Fun Colors": [
    { name: "Neon Pink", hex: "#FF10F0" },
    { name: "Neon Green", hex: "#39FF14" },
    { name: "Neon Yellow", hex: "#FFFF00" },
    { name: "Neon Orange", hex: "#FF6600" },
    { name: "Speckled", hex: "repeating-radial-gradient(circle, #fff 0px, #fff 2px, #ccc 2px, #ccc 4px)" },
    { name: "Confetti", hex: "repeating-linear-gradient(45deg, #FF6B6B 0px, #4ECDC4 10px, #FFE66D 20px, #A8E6CF 30px)" },
    { name: "Rainbow Variegated", hex: "linear-gradient(90deg, red, orange, yellow, green, blue, purple)" },
    { name: "Self-Striping Brights", hex: "repeating-linear-gradient(90deg, #FF1493 0px, #FF1493 20px, #00CED1 20px, #00CED1 40px, #FFD700 40px, #FFD700 60px)" },
  ],
};

// Helper to get all colors as flat array
export const getAllColors = () => {
  return Object.entries(colorPalette).flatMap(([family, colors]) =>
    colors.map(color => ({ ...color, family }))
  );
};

// Helper to get colors by family
export const getColorsByFamily = (family) => {
  return colorPalette[family] || [];
};

// Helper to get all families
export const getColorFamilies = () => {
  return Object.keys(colorPalette);
};

function PatternDesigner({ pattern, colors, fonts, patterns, user, supabase }) {
  const [mode, setMode] = useState("loaded");
  const [selectedPatternId, setSelectedPatternId] = useState(pattern?.id || null);

  const [manualSpecs, setManualSpecs] = useState({
    title: "",
    sourceUrl: "",
    imageUrl: "",
    originalWidth: 45,
    originalHeight: 60,
    hook: "H/8 (5.0mm)",
    gaugeStitches: 16,
    gaugeRows: 18,
    yarnBreakdown: [
      { color: "Main Color", yards: 800 }
    ]
  });

  // Color selections - stores { "Main Color": { name: "Cream", hex: "#FFFDD0", family: "Neutrals" } }
  const [colorSelections, setColorSelections] = useState({});

  // Palette colors fetched from Supabase for the active pattern
  const [paletteColors, setPaletteColors] = useState([]);

  // Save customization function
  const handleSaveCustomization = async () => {
    if (!user) {
      alert('Please sign in to save customizations.');
      return;
    }

    const customizationData = {
      user_id: user.id,
      pattern_id: activePattern?.id || null,
      yarn_colors: JSON.stringify(colorSelections),
      yarn_suggestions: JSON.stringify([]),
      notes: JSON.stringify({
        ...(mode === 'manual' && {
          title: manualSpecs.title || null,
          sourceUrl: manualSpecs.sourceUrl || null,
          imageUrl: manualSpecs.imageUrl || null,
        }),
        yarnSlots: paletteColors.map(pc => ({
          name: pc.colorway_name ? `${pc.brand_name} — ${pc.colorway_name}` : pc.brand_name || 'Unknown',
          yards: pc.yards_per_skein && pc.skeins ? Math.round(pc.yards_per_skein * pc.skeins) : null,
          role: pc.role || null,
        })),
        originalSpecs: activePattern ? {
          width: activePattern.originalSize?.width,
          height: activePattern.originalSize?.height,
          hook: activePattern.hook,
          gauge: activePattern.gauge
        } : {
          width: manualSpecs.originalWidth,
          height: manualSpecs.originalHeight,
          hook: manualSpecs.hook,
          gauge: {
            stitches: manualSpecs.gaugeStitches,
            rows: manualSpecs.gaugeRows
          }
        }
      })
    };

    const { error } = activePattern?.id
      ? await supabase
          .from('pattern_customizations')
          .upsert(customizationData, { onConflict: 'user_id,pattern_id' })
      : await supabase
          .from('pattern_customizations')
          .insert(customizationData);

    if (error) {
      console.error('Error saving customization:', error);
      alert('Failed to save. Please try again.');
    } else {
      alert('✅ Design saved! Find it in My Projects > My Designs.');
    }
  };

  // Yarn substitution picker state
  const [colorPickerOpen, setColorPickerOpen] = useState(null); // yarn object or null

  const activePattern = pattern || (selectedPatternId ? patterns.find(p => p.id === selectedPatternId) : null);

  const [customSpecs, setCustomSpecs] = useState({
    width: activePattern?.originalSize?.width || manualSpecs.originalWidth,
    height: activePattern?.originalSize?.height || manualSpecs.originalHeight,
  });

  useEffect(() => {
    if (activePattern) {
      setCustomSpecs({
        width: activePattern.originalSize?.width || 45,
        height: activePattern.originalSize?.height || 60,
      });
    } else if (mode === "manual") {
      setCustomSpecs({
        width: manualSpecs.originalWidth,
        height: manualSpecs.originalHeight,
      });
    }
  }, [activePattern, mode, manualSpecs.originalWidth, manualSpecs.originalHeight]);

  // Fetch yarn_library entries for the active pattern using yarnColors IDs
  useEffect(() => {
    const yarnColors = activePattern?.yarnColors;
    if (!yarnColors || yarnColors.length === 0) {
      setPaletteColors([]);
      return;
    }
    const ids = yarnColors.map(y => y.id).filter(Boolean);
    if (ids.length === 0) { setPaletteColors([]); return; }
    supabase
      .from('yarn_library')
      .select('*')
      .in('id', ids)
      .then(({ data }) => {
        const merged = yarnColors
          .map(yc => {
            const lib = (data || []).find(d => d.id === yc.id);
            return lib ? { ...lib, skeins: yc.skeins, role: yc.role } : null;
          })
          .filter(Boolean);
        setPaletteColors(merged);
      });
  }, [activePattern?.yarnColors]);

  const addYarnColor = () => {
    setManualSpecs({
      ...manualSpecs,
      yarnBreakdown: [...manualSpecs.yarnBreakdown, { color: "", yards: 0 }]
    });
  };

  const updateYarnColor = (index, field, value) => {
    const updated = [...manualSpecs.yarnBreakdown];
    updated[index][field] = field === "yards" ? parseInt(value) || 0 : value;
    setManualSpecs({ ...manualSpecs, yarnBreakdown: updated });
  };

  const removeYarnColor = (index) => {
    setManualSpecs({
      ...manualSpecs,
      yarnBreakdown: manualSpecs.yarnBreakdown.filter((_, i) => i !== index)
    });
  };

  // Yarn Substitution Picker Component
  const YarnSubstitutionPicker = ({ yarn, onClose }) => {
    const WEIGHT_ORDER = ['0 Lace', '1 Super Fine', '2 Fine', '3 Light', '4 Medium/Worsted', '5 Bulky', '6 Super Bulky', '7 Jumbo'];
    const [tab, setTab] = React.useState('yarn'); // 'yarn' | 'generic'
    const [allYarns, setAllYarns] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedWeight, setSelectedWeight] = React.useState(null);
    const [weightSearch, setWeightSearch] = React.useState('');
    const [selectedFamily, setSelectedFamily] = React.useState(null);

    // Load full library once on mount
    React.useEffect(() => {
      supabase
        .from('yarn_library')
        .select('id, brand_name, colorway_name, image_url, yards_per_skein, weight, fiber')
        .limit(200)
        .then(({ data }) => { setAllYarns(data || []); setLoading(false); });
    }, []);

    const handleSelectYarn = (result) => {
      setColorSelections(prev => ({
        ...prev,
        [yarn.color]: {
          name: `${result.brand_name} — ${result.colorway_name}`,
          imageUrl: result.image_url || null,
          hex: null,
          yarnId: result.id,
          weight: result.weight,
          fiber: result.fiber,
          isCustom: false,
        }
      }));
      onClose();
    };

    const handleSelectGeneric = (colorInfo, family) => {
      setColorSelections(prev => ({
        ...prev,
        [yarn.color]: { name: colorInfo.name, imageUrl: null, hex: colorInfo.hex, family, isCustom: true }
      }));
      onClose();
    };

    const getWeightBadge = (resultWeight) => {
      if (!yarn.weight || !resultWeight) return null;
      const origIdx = WEIGHT_ORDER.indexOf(yarn.weight.trim());
      const resIdx = WEIGHT_ORDER.indexOf(resultWeight.trim());
      if (origIdx === -1 || resIdx === -1 || origIdx === resIdx) return null;
      return resIdx > origIdx
        ? { label: '↑ Heavier', color: '#b45309' }
        : { label: '↓ Lighter', color: '#0369a1' };
    };

    // Weight groups for the category view — pattern's weight first, then WEIGHT_ORDER, then unspecified
    const weightGroups = React.useMemo(() => {
      const grouped = {};
      allYarns.forEach(r => {
        const key = r.weight?.trim() || '__none__';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(r);
      });
      const myWeight = yarn.weight?.trim();
      const ordered = [];
      if (myWeight && grouped[myWeight]) ordered.push(myWeight);
      WEIGHT_ORDER.forEach(w => { if (w !== myWeight && grouped[w]) ordered.push(w); });
      Object.keys(grouped).forEach(w => {
        if (w !== '__none__' && w !== myWeight && !WEIGHT_ORDER.includes(w)) ordered.push(w);
      });
      if (grouped['__none__']) ordered.push('__none__');
      return ordered.map(w => ({ weight: w, items: grouped[w] }));
    }, [allYarns, yarn.weight]);

    // Within a selected weight: filter by search, perfect matches first then rest
    const weightYarns = React.useMemo(() => {
      if (!selectedWeight) return { perfect: [], others: [] };
      const key = selectedWeight === '__none__' ? null : selectedWeight;
      const base = allYarns.filter(r => (r.weight?.trim() || null) === key);
      const filtered = weightSearch.trim()
        ? base.filter(r => `${r.brand_name} ${r.colorway_name}`.toLowerCase().includes(weightSearch.toLowerCase()))
        : base;
      const perfect = filtered.filter(r => r.fiber === yarn.fiber);
      const others = filtered.filter(r => r.fiber !== yarn.fiber);
      return { perfect, others };
    }, [selectedWeight, weightSearch, allYarns, yarn.fiber]);

    const ResultRow = ({ result, showFiberBadge }) => {
      const weightBadge = getWeightBadge(result.weight);
      const fiberMatch = showFiberBadge && yarn.fiber && result.fiber === yarn.fiber;
      const fiberDiff = showFiberBadge && yarn.fiber && result.fiber !== yarn.fiber;
      return (
        <button
          type="button"
          onClick={() => handleSelectYarn(result)}
          style={{ display: "flex", alignItems: "center", gap: "0.75rem", width: "100%", padding: "0.6rem 0.75rem", background: "transparent", border: `1px solid ${colors.borderGreige}`, borderRadius: "8px", cursor: "pointer", marginBottom: "0.4rem", textAlign: "left" }}
          onMouseEnter={e => e.currentTarget.style.background = colors.bgCream}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          {result.image_url
            ? <img src={result.image_url} alt="" style={{ width: "40px", height: "40px", borderRadius: "6px", objectFit: "cover", flexShrink: 0 }} />
            : <div style={{ width: "40px", height: "40px", borderRadius: "6px", background: colors.borderGreige, flexShrink: 0 }} />
          }
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: "600", fontSize: "0.875rem", color: colors.charcoal }}>{result.brand_name} — {result.colorway_name}</div>
            <div style={{ fontSize: "0.75rem", color: colors.brownGray }}>
              {[result.weight, result.fiber, result.yards_per_skein ? `${result.yards_per_skein} yds/skein` : null].filter(Boolean).join(' · ')}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px", flexShrink: 0 }}>
            {weightBadge && <span style={{ fontSize: "0.68rem", fontWeight: "700", color: weightBadge.color, whiteSpace: "nowrap" }}>{weightBadge.label}</span>}
            {fiberMatch && <span style={{ fontSize: "0.68rem", fontWeight: "700", color: colors.sageGreen, whiteSpace: "nowrap" }}>✓ Same fiber</span>}
            {fiberDiff && <span style={{ fontSize: "0.68rem", fontWeight: "700", color: colors.brownGray, whiteSpace: "nowrap" }}>~ Diff. fiber</span>}
          </div>
        </button>
      );
    };

    const tabBtn = (id, label) => (
      <button
        onClick={() => setTab(id)}
        style={{ flex: 1, padding: "0.5rem", border: "none", borderBottom: `2px solid ${tab === id ? colors.sageGreen : colors.borderGreige}`, background: "transparent", fontWeight: tab === id ? "700" : "500", color: tab === id ? colors.sageGreen : colors.brownGray, cursor: "pointer", fontSize: "0.875rem" }}
      >{label}</button>
    );

    return (
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "16px", padding: "2rem", maxWidth: "520px", width: "90%", maxHeight: "82vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
            <h3 style={{ fontSize: "1.3rem", fontFamily: fonts.display, color: colors.charcoal, margin: 0 }}>Choose a Color</h3>
            <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.5rem", color: colors.brownGray, cursor: "pointer" }}>×</button>
          </div>

          <div style={{ fontSize: "0.85rem", color: colors.brownGray, marginBottom: "1rem", padding: "0.5rem 0.75rem", background: colors.bgCream, borderRadius: "8px" }}>
            For: <strong>{yarn.color}</strong>{yarn.weight && <> · {yarn.weight}</>}{yarn.fiber && <> · {yarn.fiber}</>}
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", marginBottom: "1.25rem", borderBottom: `2px solid ${colors.borderGreige}` }}>
            {tabBtn('yarn', 'Yarn Library')}
            {tabBtn('generic', 'Generic Colors')}
          </div>

          {/* YARN LIBRARY TAB */}
          {tab === 'yarn' && (
            <div>
              {loading ? (
                <div style={{ textAlign: "center", padding: "2rem", color: colors.brownGray }}>Loading...</div>
              ) : !selectedWeight ? (
                /* Weight category list */
                <div style={{ display: "grid", gap: "0.5rem" }}>
                  {weightGroups.map(({ weight, items }) => {
                    const label = weight === '__none__' ? 'Unspecified Weight' : weight;
                    const isMatch = weight === yarn.weight;
                    const badge = weight !== '__none__' ? getWeightBadge(weight) : null;
                    const previews = items.slice(0, 4).filter(r => r.image_url);
                    return (
                      <button
                        key={weight}
                        onClick={() => { setSelectedWeight(weight); setWeightSearch(''); }}
                        style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", background: isMatch ? `${colors.sageGreen}15` : colors.bgCream, border: `2px solid ${isMatch ? colors.sageGreen : colors.borderGreige}`, borderRadius: "10px", cursor: "pointer", textAlign: "left" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = colors.sageGreen; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = isMatch ? colors.sageGreen : colors.borderGreige; }}
                      >
                        <div style={{ display: "flex", gap: "3px", flexShrink: 0 }}>
                          {previews.length > 0
                            ? previews.map((r, i) => <img key={i} src={r.image_url} alt="" style={{ width: "22px", height: "22px", borderRadius: "4px", objectFit: "cover" }} />)
                            : <div style={{ width: "22px", height: "22px", borderRadius: "4px", background: colors.borderGreige }} />
                          }
                        </div>
                        <span style={{ fontWeight: "600", fontSize: "0.9rem", color: colors.charcoal, flex: 1 }}>{label}</span>
                        {isMatch && <span style={{ fontSize: "0.72rem", fontWeight: "700", color: colors.sageGreen }}>✓ Your weight</span>}
                        {badge && <span style={{ fontSize: "0.72rem", fontWeight: "700", color: badge.color }}>{badge.label}</span>}
                        <span style={{ fontSize: "0.8rem", color: colors.brownGray, flexShrink: 0 }}>{items.length} →</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Drilled into a weight */
                <div>
                  <button onClick={() => { setSelectedWeight(null); setWeightSearch(''); }} style={{ background: "none", border: "none", color: colors.sageGreen, fontWeight: "600", cursor: "pointer", marginBottom: "0.75rem", fontSize: "0.9rem", padding: 0 }}>
                    ← {selectedWeight === '__none__' ? 'Unspecified Weight' : selectedWeight}
                  </button>
                  <input
                    type="text"
                    placeholder="Search within this weight..."
                    value={weightSearch}
                    onChange={e => setWeightSearch(e.target.value)}
                    autoFocus
                    style={{ width: "100%", padding: "0.6rem", border: `2px solid ${colors.borderGreige}`, borderRadius: "8px", fontSize: "0.875rem", outline: "none", marginBottom: "1rem", boxSizing: "border-box" }}
                  />
                  {(() => {
                    const all = [...weightYarns.perfect, ...weightYarns.others];
                    const byBrand = {};
                    all.forEach(r => {
                      if (!byBrand[r.brand_name]) byBrand[r.brand_name] = [];
                      byBrand[r.brand_name].push(r);
                    });
                    const sorted = Object.entries(byBrand).sort(([, a], [, b]) => {
                      const aMatch = a.some(r => r.fiber === yarn.fiber) ? 0 : 1;
                      const bMatch = b.some(r => r.fiber === yarn.fiber) ? 0 : 1;
                      return aMatch - bMatch;
                    });
                    return sorted.map(([brand, items]) => (
                      <div key={brand}>
                        <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", letterSpacing: "0.05em", padding: "0.5rem 0 0.25rem" }}>{brand}</div>
                        {items.map(r => <ResultRow key={r.id} result={r} showFiberBadge={true} />)}
                      </div>
                    ));
                  })()}
                  {weightYarns.perfect.length === 0 && weightYarns.others.length === 0 && (
                    <div style={{ textAlign: "center", padding: "1.5rem", color: colors.brownGray, fontSize: "0.9rem" }}>No yarns match your search.</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* GENERIC COLORS TAB */}
          {tab === 'generic' && (
            <div>
              <div style={{ fontSize: "0.8rem", color: colors.brownGray, marginBottom: "1rem" }}>
                Generic colors are for design exploration only and won't link to a purchasable yarn.
              </div>
              {!selectedFamily ? (
                <div style={{ display: "grid", gap: "0.5rem" }}>
                  {getColorFamilies().map(family => {
                    const preview = getColorsByFamily(family).slice(0, 5);
                    return (
                      <button
                        key={family}
                        onClick={() => setSelectedFamily(family)}
                        style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", background: colors.bgCream, border: `2px solid ${colors.borderGreige}`, borderRadius: "10px", cursor: "pointer", textAlign: "left" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = colors.sageGreen; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = colors.borderGreige; }}
                      >
                        <div style={{ display: "flex", gap: "3px", flexShrink: 0 }}>
                          {preview.map((c, i) => (
                            <div key={i} style={{ width: "18px", height: "18px", borderRadius: "4px", background: c.hex, boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }} />
                          ))}
                        </div>
                        <span style={{ fontWeight: "600", fontSize: "0.9rem", color: colors.charcoal }}>{family}</span>
                        <span style={{ marginLeft: "auto", fontSize: "0.8rem", color: colors.brownGray }}>→</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <button onClick={() => setSelectedFamily(null)} style={{ background: "none", border: "none", color: colors.sageGreen, fontWeight: "600", cursor: "pointer", marginBottom: "1rem", fontSize: "0.9rem", padding: 0 }}>
                    ← {selectedFamily}
                  </button>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: "0.75rem" }}>
                    {getColorsByFamily(selectedFamily).map(colorInfo => {
                      const isSelected = colorSelections[yarn.color]?.name === colorInfo.name;
                      return (
                        <div
                          key={colorInfo.name}
                          onClick={() => handleSelectGeneric(colorInfo, selectedFamily)}
                          style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", padding: "0.4rem", borderRadius: "10px", border: `2px solid ${isSelected ? colors.sageGreen : 'transparent'}` }}
                          onMouseEnter={e => e.currentTarget.style.border = `2px solid ${colors.sageGreen}`}
                          onMouseLeave={e => e.currentTarget.style.border = `2px solid ${isSelected ? colors.sageGreen : 'transparent'}`}
                        >
                          <div style={{ width: "60px", height: "60px", background: colorInfo.hex, borderRadius: "8px", marginBottom: "0.35rem", boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }} />
                          <div style={{ fontSize: "0.7rem", fontWeight: "500", color: colors.charcoal, textAlign: "center", lineHeight: 1.2 }}>{colorInfo.name}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "16px",
        padding: "2rem",
        boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
      }}
    >
      <div style={{ marginBottom: "2rem" }}>
        <h2
          style={{
            fontSize: "2rem",
            fontFamily: fonts.display,
            color: colors.charcoal,
            marginBottom: "0.5rem",
          }}
        >
          Pattern Designer
        </h2>
        <p style={{ color: colors.darkGray, fontSize: "0.95rem" }}>
          Customize size, pick colors & calculate yarn needs
        </p>
      </div>

      {/* Mode Tabs — manual entry removed, replaced by Add New Pattern in picker */}

      {/* Yarn Substitution Picker Modal */}
      {colorPickerOpen && (
        <YarnSubstitutionPicker
          yarn={colorPickerOpen}
          onClose={() => setColorPickerOpen(null)}
        />
      )}

      {/* LOAD PATTERN MODE */}
      {mode === "loaded" && (
        <div>
          {!activePattern ? (
            <div>
              <label style={{ display: "block", marginBottom: "0.75rem", fontWeight: "600", color: colors.brownGray, fontSize: "0.9rem" }}>
                Select a Pattern
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "0.75rem" }}>
                {[
                  ...patterns.filter(p => p.submittedBy === user?.id),
                  ...patterns.filter(p => p.status === 'published' && p.submittedBy !== user?.id),
                ].map(p => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPatternId(p.id)}
                    style={{
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: `2px solid ${colors.borderGreige}`,
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = colors.sageGreen; e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = colors.borderGreige; e.currentTarget.style.transform = "none"; }}
                  >
                    {p.image
                      ? <img src={p.image} alt={p.title} style={{ width: "100%", height: "110px", objectFit: "cover", display: "block" }} />
                      : <div style={{ width: "100%", height: "110px", background: colors.bgCream, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2rem" }}>🧶</div>
                    }
                    <div style={{ padding: "0.5rem 0.6rem", background: "#fff" }}>
                      <div style={{ fontSize: "0.82rem", fontWeight: "700", color: colors.charcoal, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                      <div style={{ fontSize: "0.75rem", color: colors.brownGray }}>{p.yarnWeight || p.difficulty}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              {/* Original Pattern Reference Photo */}
              {/* Original Specs Summary */}
              <div
                style={{
                  background: colors.bgCream,
                  padding: "1.5rem",
                  borderRadius: "12px",
                  marginBottom: "2rem",
                  border: `2px solid ${colors.borderGreige}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                  <h3
                    style={{
                      fontSize: "1.1rem",
                      color: colors.charcoal,
                      margin: 0,
                      fontWeight: "700",
                    }}
                  >
                    {activePattern.title}
                  </h3>
                  <button
                    onClick={() => {
                      setSelectedPatternId(null);
                      setColorSelections({});
                      setPaletteColors([]);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      color: colors.sageGreen,
                      cursor: "pointer",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    ← Back to patterns
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "1rem" }}>
                  {activePattern.originalSize?.width && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Size</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>{activePattern.originalSize.width}" × {activePattern.originalSize.height}"</div>
                    </div>
                  )}
                  {activePattern.difficulty && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Difficulty</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>{activePattern.difficulty}</div>
                    </div>
                  )}
                  {activePattern.hook?.length > 0 && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Hook Size</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>{Array.isArray(activePattern.hook) ? activePattern.hook.join(", ") : activePattern.hook}</div>
                    </div>
                  )}
                  {activePattern.yarnWeight && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Yarn Weight</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>{activePattern.yarnWeight}</div>
                    </div>
                  )}
                  {activePattern.yarnMaterial && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Yarn Material</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>{activePattern.yarnMaterial}</div>
                    </div>
                  )}
                  {activePattern.constructionMethod && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Construction</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>{activePattern.constructionMethod}</div>
                    </div>
                  )}
                  {activePattern.gaugeEntries?.length > 0 && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Gauge</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>
                        {activePattern.gaugeEntries.map((entry, idx) => {
                          const unitLabel = entry.unit === '2in' ? '2"' : '4"';
                          let text = '';
                          if (entry.type === 'standard') text = `${entry.stitches} st${entry.rows ? ` × ${entry.rows} rows` : ''} = ${unitLabel}`;
                          else if (entry.type === 'granny') text = `Finished square = ${entry.square_inches}"`;
                          else if (entry.type === 'c2c') text = `${entry.blocks} blocks = ${unitLabel}`;
                          else if (entry.type === 'note') text = entry.note || '';
                          const label = entry.label ? ` (${entry.label})` : '';
                          return <div key={idx} style={idx > 0 ? { marginTop: "0.25rem" } : {}}>{text}{label}</div>;
                        })}
                      </div>
                    </div>
                  )}
                  {activePattern.totalYards > 0 && (
                    <div>
                      <div style={{ fontSize: "0.75rem", fontWeight: "700", color: colors.brownGray, textTransform: "uppercase", marginBottom: "0.35rem" }}>Total Yarn</div>
                      <div style={{ fontSize: "1rem", fontWeight: "700", color: colors.charcoal }}>{activePattern.totalYards} yards</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Color Selection + Reference Image — two columns */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "1.5rem", alignItems: "center", marginBottom: "2rem" }}>

                {/* Left: yarn cards */}
                <div>
                <h3
                  style={{
                    fontSize: "1.3rem",
                    color: colors.charcoal,
                    marginBottom: "1rem",
                    fontWeight: "700",
                  }}
                >
                  🎨 Pick Your Colors
                </h3>

                <div style={{ display: "grid", gap: "1rem" }}>
                  {(paletteColors.length > 0
                    ? paletteColors.map(pc => ({
                        color: pc.colorway_name ? `${pc.brand_name} — ${pc.colorway_name}` : pc.brand_name || 'Unknown',
                        imageUrl: pc.image_url || null,
                        role: pc.role || null,
                        weight: pc.weight || null,
                        fiber: pc.fiber || null,
                        yards: pc.yards_per_skein && pc.skeins ? Math.round(pc.yards_per_skein * pc.skeins) : null,
                      }))
                    : []
                  ).map((yarn, idx) => {
                    const selectedColor = colorSelections[yarn.color];
                    return (
                      <div
                        key={idx}
                        style={{
                          background: colors.bgCream,
                          padding: "1.25rem",
                          borderRadius: "12px",
                          border: `2px solid ${colors.borderGreige}`,
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                        }}
                      >
                        {/* Color Swatch — shows yarn image if available, selected hex color otherwise */}
                        <div
                          onClick={() => setColorPickerOpen(yarn)}
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "12px",
                            cursor: "pointer",
                            flexShrink: 0,
                            border: `3px solid ${selectedColor ? colors.sageGreen : colors.borderGreige}`,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            transition: "all 0.2s",
                            overflow: "hidden",
                            background: selectedColor?.isCustom ? selectedColor.hex : colors.borderGreige,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "2rem",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                          onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                        >
                          {selectedColor
                            ? (selectedColor.isCustom ? null : selectedColor.imageUrl
                                ? <img src={selectedColor.imageUrl} alt={selectedColor.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                : "🎨")
                            : yarn.imageUrl
                              ? <img src={yarn.imageUrl} alt={yarn.color} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              : "🎨"
                          }
                        </div>

                        {/* Color Info */}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: "1.1rem", fontWeight: "700", color: colors.charcoal, marginBottom: "0.25rem" }}>
                            {yarn.color}
                          </div>
                          {yarn.role && (
                            <div style={{ fontSize: "0.78rem", color: colors.dustyRust, fontWeight: "600", marginBottom: "0.2rem" }}>
                              {yarn.role}
                            </div>
                          )}
                          {yarn.yards && (
                            <div style={{ fontSize: "0.78rem", color: colors.brownGray, marginBottom: "0.4rem" }}>
                              pattern calls for ~{yarn.yards} yds
                            </div>
                          )}
                          <button
                            onClick={() => setColorPickerOpen(yarn)}
                            style={{
                              padding: "0.5rem 1rem",
                              background: selectedColor ? "#fff" : colors.sageGreen,
                              color: selectedColor ? colors.sageGreen : "#fff",
                              border: `2px solid ${colors.sageGreen}`,
                              borderRadius: "50px",
                              fontSize: "0.85rem",
                              fontWeight: "600",
                              cursor: "pointer",
                            }}
                          >
                            {selectedColor ? selectedColor.name : "Choose Color"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

              </div>

                {/* Right: reference image — sticky so it stays visible while scrolling yarn list */}
                {activePattern.image && (
                  <div style={{ width: "200px", flexShrink: 0, position: "sticky", top: "1rem", alignSelf: "center" }}>
                    <div style={{ borderRadius: "12px", overflow: "hidden", border: `2px solid ${colors.borderGreige}` }}>
                      <img
                        src={activePattern.image}
                        alt={activePattern.title}
                        style={{ width: "100%", height: "260px", objectFit: "cover", display: "block" }}
                      />
                    </div>
                    <div style={{ fontSize: "0.75rem", color: colors.brownGray, marginTop: "0.4rem", textAlign: "center" }}>
                      Original
                    </div>
                  </div>
                )}

              </div>

              {/* Color Palette Summary */}
              {Object.keys(colorSelections).length > 0 && (
                <div style={{ background: colors.bgCream, borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', border: `2px solid ${colors.borderGreige}` }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: colors.charcoal, marginBottom: '1rem' }}>Your Color Palette</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {Object.entries(colorSelections).map(([slotName, sel]) => (
                      <div key={slotName} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: sel.isCustom ? sel.hex : colors.borderGreige, border: `1px solid ${colors.borderGreige}`, flexShrink: 0, overflow: 'hidden' }}>
                          {!sel.isCustom && sel.imageUrl && <img src={sel.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85rem', color: colors.brownGray }}>{slotName}</div>
                          <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.charcoal }}>{sel.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
                <button
                  onClick={() => {
                    const entries = Object.entries(colorSelections).map(([slot, sel]) => {
                      const pc = paletteColors.find(p => (p.colorway_name ? `${p.brand_name} — ${p.colorway_name}` : p.brand_name) === slot);
                      const yards = pc?.yards_per_skein && pc?.skeins ? Math.round(pc.yards_per_skein * pc.skeins) : null;
                      return { slot, sel, yards };
                    });
                    const originalLines = entries.map(({ slot, yards }) => `  ${slot}${yards ? ` (~${yards} yds)` : ''}`).join('\n');
                    const subLines = entries.map(({ slot, sel }) => `  ${sel.name} (for ${slot})`).join('\n');
                    const summary = `Color Palette: ${activePattern.title}\n\nOriginal Yarns:\n${originalLines}\n\nYour Substitutions:\n${subLines}\n\nOriginal Pattern: ${activePattern.sourceUrl || ''}\n\n⚠️ Yardage based on original pattern yarn. If substituting a different weight, amounts may vary.`;
                    navigator.clipboard.writeText(summary);
                    alert('✅ Color palette copied!');
                  }}
                  style={{
                    flex: 1,
                    padding: "1rem",
                    background: "#fff",
                    border: `2px solid ${colors.sageGreen}`,
                    borderRadius: "12px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: colors.sageGreen,
                    cursor: "pointer",
                  }}
                >
                  📋 Copy Specs
                </button>

                <button
                  onClick={handleSaveCustomization}
                  style={{
                    flex: 1,
                    padding: "1rem",
                    background: `linear-gradient(135deg, ${colors.mustard} 0%, #c89b68 100%)`,
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  💾 Save Custom
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Manual Entry Mode */}
      {mode === "manual" && (
        <div>
          {/* Manual Entry Form */}
          <div
            style={{
              background: colors.bgCream,
              padding: "1.5rem",
              borderRadius: "12px",
              marginBottom: "2rem",
              border: `2px solid ${colors.borderGreige}`,
            }}
          >
            <h3
              style={{
                fontSize: "1.3rem",
                color: colors.charcoal,
                marginBottom: "1rem",
                fontWeight: "700",
              }}
            >
              Pattern Information
            </h3>

            {/* Pattern Name */}
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: colors.brownGray,
                  fontSize: "0.9rem",
                }}
              >
                Pattern Name (optional)
              </label>
              <input
                type="text"
                value={manualSpecs.title}
                onChange={(e) =>
                  setManualSpecs({ ...manualSpecs, title: e.target.value })
                }
                placeholder="e.g., Cozy Granny Square Blanket"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  outline: "none",
                }}
              />
            </div>

            {/* Pattern URL */}
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: colors.brownGray,
                  fontSize: "0.9rem",
                }}
              >
                Pattern URL (optional)
              </label>
              <input
                type="text"
                value={manualSpecs.sourceUrl}
                onChange={(e) =>
                  setManualSpecs({ ...manualSpecs, sourceUrl: e.target.value })
                }
                placeholder="https://..."
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  outline: "none",
                }}
              />
            </div>

            {/* Image URL */}
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: colors.brownGray,
                  fontSize: "0.9rem",
                }}
              >
                Image URL (optional)
              </label>
              <input
                type="text"
                value={manualSpecs.imageUrl}
                onChange={(e) =>
                  setManualSpecs({ ...manualSpecs, imageUrl: e.target.value })
                }
                placeholder="https://... (direct link to pattern photo)"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  outline: "none",
                }}
              />
            </div>

            {/* Original Dimensions */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Original Width (inches) *
                </label>
                <input
                  type="number"
                  value={manualSpecs.originalWidth || ''}
                  onChange={(e) =>
                    setManualSpecs({ ...manualSpecs, originalWidth: parseInt(e.target.value) || 0 })
                  }
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                    fontWeight: "600",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Original Height (inches) *
                </label>
                <input
                  type="number"
                  value={manualSpecs.originalHeight || ''}
                  onChange={(e) =>
                    setManualSpecs({ ...manualSpecs, originalHeight: parseInt(e.target.value) || 0 })
                  }
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                    fontWeight: "600",
                  }}
                />
              </div>
            </div>

            {/* Gauge */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Stitches per 4" *
                </label>
                <input
                  type="number"
                  value={manualSpecs.gaugeStitches || ''}
                  onChange={(e) =>
                    setManualSpecs({ ...manualSpecs, gaugeStitches: parseInt(e.target.value) || 0 })
                  }
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                    fontWeight: "600",
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: colors.brownGray,
                    fontSize: "0.9rem",
                  }}
                >
                  Rows per 4" *
                </label>
                <input
                  type="number"
                  value={manualSpecs.gaugeRows || ''}
                  onChange={(e) =>
                    setManualSpecs({ ...manualSpecs, gaugeRows: parseInt(e.target.value) || 0 })
                  }
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `2px solid ${colors.borderGreige}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                    fontWeight: "600",
                  }}
                />
              </div>
            </div>

            {/* Hook Size */}
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: colors.brownGray,
                  fontSize: "0.9rem",
                }}
              >
                Hook Size *
              </label>
              <select
                value={manualSpecs.hook}
                onChange={(e) =>
                  setManualSpecs({ ...manualSpecs, hook: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: `2px solid ${colors.borderGreige}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  outline: "none",
                }}
              >
                <option value="B/1 (2.25mm)">B/1 (2.25mm)</option>
                <option value="C/2 (2.75mm)">C/2 (2.75mm)</option>
                <option value="D/3 (3.25mm)">D/3 (3.25mm)</option>
                <option value="E/4 (3.5mm)">E/4 (3.5mm)</option>
                <option value="F/5 (3.75mm)">F/5 (3.75mm)</option>
                <option value="G/6 (4.0mm)">G/6 (4.0mm)</option>
                <option value="7 (4.5mm)">7 (4.5mm)</option>
                <option value="H/8 (5.0mm)">H/8 (5.0mm)</option>
                <option value="I/9 (5.5mm)">I/9 (5.5mm)</option>
                <option value="J/10 (6.0mm)">J/10 (6.0mm)</option>
                <option value="K/10.5 (6.5mm)">K/10.5 (6.5mm)</option>
                <option value="L/11 (8.0mm)">L/11 (8.0mm)</option>
                <option value="M/13 (9.0mm)">M/13 (9.0mm)</option>
                <option value="N/15 (10.0mm)">N/15 (10.0mm)</option>
                <option value="P/16 (11.5mm)">P/16 (11.5mm)</option>
                <option value="Q (15.0mm)">Q (15.0mm)</option>
                <option value="S (19.0mm)">S (19.0mm)</option>
              </select>
            </div>

            {/* Yarn Breakdown */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: colors.brownGray,
                  fontSize: "0.9rem",
                }}
              >
                Yarn Breakdown *
              </label>
              {manualSpecs.yarnBreakdown.map((yarn, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr auto",
                    gap: "0.5rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  <input
                    type="text"
                    value={yarn.color}
                    onChange={(e) => {
                      const updated = [...manualSpecs.yarnBreakdown];
                      updated[idx].color = e.target.value;
                      setManualSpecs({ ...manualSpecs, yarnBreakdown: updated });
                    }}
                    placeholder="Color name (e.g., Main Color)"
                    style={{
                      padding: "0.75rem",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      fontSize: "0.95rem",
                      outline: "none",
                    }}
                  />
                  <input
                    type="number"
                    value={yarn.yards || ''}
                    onChange={(e) => {
                      const updated = [...manualSpecs.yarnBreakdown];
                      updated[idx].yards = parseInt(e.target.value) || 0;
                      setManualSpecs({ ...manualSpecs, yarnBreakdown: updated });
                    }}
                    placeholder="Yards"
                    style={{
                      padding: "0.75rem",
                      border: `2px solid ${colors.borderGreige}`,
                      borderRadius: "8px",
                      fontSize: "0.95rem",
                      outline: "none",
                    }}
                  />
                  {manualSpecs.yarnBreakdown.length > 1 && (
                    <button
                      onClick={() => {
                        setManualSpecs({
                          ...manualSpecs,
                          yarnBreakdown: manualSpecs.yarnBreakdown.filter((_, i) => i !== idx)
                        });
                      }}
                      style={{
                        padding: "0.75rem",
                        background: colors.dustyRust,
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "1rem",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => {
                  setManualSpecs({
                    ...manualSpecs,
                    yarnBreakdown: [...manualSpecs.yarnBreakdown, { color: "", yards: 0 }]
                  });
                }}
                style={{
                  padding: "0.75rem 1rem",
                  background: "#fff",
                  border: `2px solid ${colors.sageGreen}`,
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  color: colors.sageGreen,
                  cursor: "pointer",
                }}
              >
                + Add Color
              </button>
            </div>
          </div>

          {/* Now show the same sections as Load Pattern mode */}
          {/* Color Selection Cards */}
          <div style={{ marginBottom: "2rem" }}>
            <h3
              style={{
                fontSize: "1.3rem",
                color: colors.charcoal,
                marginBottom: "1rem",
                fontWeight: "700",
              }}
            >
              🎨 Pick Your Colors
            </h3>

            <div style={{ display: "grid", gap: "1rem" }}>
              {manualSpecs.yarnBreakdown.map((yarn, idx) => {
                const selectedColor = colorSelections[yarn.color];
                return (
                  <div
                    key={idx}
                    style={{
                      background: colors.bgCream,
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: `2px solid ${colors.borderGreige}`,
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    {/* Color Swatch */}
                    <div
                      onClick={() => setColorPickerOpen(yarn)}
                      style={{
                        width: "80px",
                        height: "80px",
                        background: selectedColor?.isCustom ? selectedColor.hex : colors.borderGreige,
                        borderRadius: "12px",
                        cursor: "pointer",
                        flexShrink: 0,
                        border: `3px solid ${selectedColor ? colors.sageGreen : colors.borderGreige}`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "all 0.2s",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "2rem",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                      onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                    >
                      {selectedColor
                        ? (selectedColor.isCustom ? null : selectedColor.imageUrl
                            ? <img src={selectedColor.imageUrl} alt={selectedColor.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : "🎨")
                        : "🎨"
                      }
                    </div>

                    {/* Color Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "1.1rem", fontWeight: "700", color: colors.charcoal, marginBottom: "0.25rem" }}>
                        {yarn.color}
                      </div>
                      <button
                        onClick={() => setColorPickerOpen(yarn)}
                        style={{
                          padding: "0.5rem 1rem",
                          background: selectedColor ? "#fff" : colors.sageGreen,
                          color: selectedColor ? colors.sageGreen : "#fff",
                          border: `2px solid ${colors.sageGreen}`,
                          borderRadius: "50px",
                          fontSize: "0.85rem",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        {selectedColor ? selectedColor.name : "Choose Color"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Color Palette Summary */}
          {Object.keys(colorSelections).length > 0 && (
            <div style={{ background: colors.bgCream, borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', border: `2px solid ${colors.borderGreige}` }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: colors.charcoal, marginBottom: '1rem' }}>Your Color Palette</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {Object.entries(colorSelections).map(([slotName, sel]) => (
                  <div key={slotName} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: sel.isCustom ? sel.hex : colors.borderGreige, border: `1px solid ${colors.borderGreige}`, flexShrink: 0, overflow: 'hidden' }}>
                      {!sel.isCustom && sel.imageUrl && <img src={sel.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: colors.brownGray }}>{slotName}</div>
                      <div style={{ fontSize: '0.95rem', fontWeight: '600', color: colors.charcoal }}>{sel.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
            <button
              onClick={() => {
                const entries = Object.entries(colorSelections).map(([slot, sel]) => {
                  const pc = paletteColors.find(p => (p.colorway_name ? `${p.brand_name} — ${p.colorway_name}` : p.brand_name) === slot);
                  const yards = pc?.yards_per_skein && pc?.skeins ? Math.round(pc.yards_per_skein * pc.skeins) : null;
                  return { slot, sel, yards };
                });
                const originalLines = entries.map(({ slot, yards }) => `  ${slot}${yards ? ` (~${yards} yds)` : ''}`).join('\n');
                const subLines = entries.map(({ slot, sel }) => `  ${sel.name} (for ${slot})`).join('\n');
                const summary = `Color Palette: ${manualSpecs.title || 'My Pattern'}\n\nOriginal Yarns:\n${originalLines}\n\nYour Substitutions:\n${subLines}\n\nOriginal Pattern: ${manualSpecs.sourceUrl || ''}\n\n⚠️ Yardage based on original pattern yarn. If substituting a different weight, amounts may vary.`;
                navigator.clipboard.writeText(summary);
                alert('✅ Color palette copied!');
              }}
              style={{
                flex: 1,
                padding: "1rem",
                background: "#fff",
                border: `2px solid ${colors.sageGreen}`,
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "600",
                color: colors.sageGreen,
                cursor: "pointer",
              }}
            >
              📋 Copy Specs
            </button>

            <button
              onClick={handleSaveCustomization}
              style={{
                flex: 1,
                padding: "1rem",
                background: `linear-gradient(135deg, ${colors.mustard} 0%, #c89b68 100%)`,
                border: "none",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "600",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              💾 Save Custom
            </button>
          </div>
        </div>
      )}


    </div>
  );
}

export default PatternDesigner;
